import ModalForm from '../../../../../components/ui/ModalForm';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import WarningModal from '../../../../../components/ui/modal/WarningModal';
import { Button, Form, Table, Tooltip } from 'antd';
import { useReceivingReportStore } from '../../../../../store/ReceivingReportStore';
import React, { useEffect, useState } from 'react';
import { useFetchData } from '../../../../../hooks';
import {
  BaseModel,
  FormField,
  PageableResponse,
  Proder,
  ProductModel,
  toProductMode,
} from '../../../../../types';
import { Resource } from '../../../../../lib/enums';
import { RRMovement } from '../../../../../types/ReceivingReport';
import { roundAndFormat } from '../../../../../lib/utils';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  useFetchProderProducts,
  useFetchProderSKUS,
} from '../../../../../hooks/useMaterialRequestHooks';

const ReceivingReportMovementUpdate = () => {
  const [form] = Form.useForm();
  const receivingReportStore = useReceivingReportStore();

  const [modalVisible, setModalVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [uuidDelete, setUuidDelete] = useState('');

  const [proderCode, setProderCode] = useState<string | null>(null);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuOptions, setSkuOptions] = useState<BaseModel[] | undefined>([]);
  const [proderId, setProderId] = useState<number | null>(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productOptions, setProductOptions] = useState<
    ProductModel[] | undefined
  >([]);

  const { data: proderData, isLoading: isProderLoading } = useFetchData<
    PageableResponse<Proder>
  >('/proders', {
    resource: Resource.FULL,
    length: parseInt('999999'),
  });

  const { data: productData, isLoading: isProductLoading } = useFetchData<
    PageableResponse<ProductModel>
  >('/product/products', {
    resource: Resource.FULL,
    length: parseInt('999999'),
  });

  const { data: uomData, isLoading: isUomLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/product/uoms', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const proderOptions = proderData?.data.map((item) => ({
    id: item.id,
    code: item.proderno,
    description: item.proderno,
  })) as BaseModel[];

  const { data: proderProductData, isLoading: isProderProductLoading } =
    useFetchProderProducts(proderId);
  const { data: proderSkuData, isLoading: isProderSkuLoading } =
    useFetchProderSKUS(proderCode);

  useEffect(() => {
    setProductLoading(isProductLoading);
    if (!isProductLoading) {
      setProductOptions(productData?.data);
    }
    if (!isProderProductLoading && proderProductData !== undefined) {
      setProductLoading(isProderProductLoading);
      setProductOptions(proderProductData);
    }

    setSkuLoading(isProderSkuLoading);
    if (!isProderSkuLoading) {
      setSkuOptions([]);
    }
    if (!isProderSkuLoading && proderSkuData !== undefined) {
      setSkuLoading(isProderSkuLoading);
      setSkuOptions(proderSkuData);
    }
  }, [
    isProductLoading,
    isProderProductLoading,
    productOptions,
    isProderSkuLoading,
    proderSkuData,
    skuOptions,
    proderCode,
  ]);

  const handleOpenModal = () => {
    setModalVisible(true);
    form.resetFields();
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleEditButtonClick = (record: RRMovement) => {
    setModalVisible(true);
    receivingReportStore.setMovement(record);
  };
  const handleDeleteButtonClick = (record: RRMovement) => {
    setUuidDelete(record.uuid);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteModalConfirm = () => {
    receivingReportStore.removeMovement(uuidDelete);
    setIsDeleteModalOpen(false);
    setIsInfoModalOpen(true);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
  };

  const onValuesChange = (changedFields: any) => {
    if (changedFields.proderNo) {
      setProderId(changedFields.proderNo);
      setProderCode(
        proderData?.data?.find((obj) => obj.id === changedFields.proderNo)
          ?.proderno ?? null,
      );
      setProductLoading(true);
      setSkuLoading(true);
    }

    if (changedFields.product) {
      const product: ProductModel | undefined = productData?.data?.find(
        (obj) => {
          return obj.id == changedFields.product;
        },
      );
      form.setFieldValue('productUom', product?.defaultUom?.id);
    }
  };

  const onFinish = (value: any) => {
    let product: BaseModel | undefined = productData?.data.find((obj) => {
      return obj.id == value.product;
    });
    let productUom: BaseModel | undefined = uomData?.data.find((obj) => {
      return obj.id == value.productUom;
    });

    if (product === undefined) {
      const recProduct = receivingReportStore.formData.movements.map((item) =>
        toProductMode(item.product!),
      );

      product = recProduct.find((obj) => {
        return obj.id == value.product;
      });
    }

    if (productUom === undefined) {
      const recUoM = receivingReportStore.formData.movements.map(
        (item) => item.requestUom!,
      );

      productUom = recUoM.find((obj) => {
        return obj.id == value.requestUom;
      });
    }

    const proderNo: BaseModel | undefined = proderOptions?.find((obj) => {
      return obj.id == value.proderNo;
    });

    const skuNo: BaseModel | undefined = skuOptions?.find((obj) => {
      return obj.id == value.sku;
    });

    const requestLine = {
      ...receivingReportStore.movement,
      ...value,
      product: product,
      productUom: productUom,
      proderNo: proderNo?.code,
      sku: skuNo?.code,
      uuid:
        receivingReportStore?.movement?.uuid === undefined ||
        receivingReportStore?.movement?.uuid === null ||
        receivingReportStore?.movement?.uuid === ''
          ? ''
          : receivingReportStore.movement?.uuid,
    };
    if (
      requestLine.uuid === undefined ||
      requestLine.uuid === null ||
      requestLine.uuid === ''
    ) {
      receivingReportStore.addMovement(requestLine);
    } else {
      receivingReportStore.editMovement(requestLine.uuid, requestLine);
    }
    setModalVisible(false);
  };

  const fields: FormField[] = [
    {
      name: 'proderNo',
      label: 'Proder',
      isMultiple: false,
      type: 'select',
      isLoading: isProderLoading,
      values: proderOptions,
    },
    {
      name: 'sku',
      label: 'SKU',
      isMultiple: false,
      type: 'select',
      values: skuOptions,
      isLoading: skuLoading,
      rules: [{ required: true, message: 'Please select SKU' }],
      defaultValue: receivingReportStore.movement?.sku,
    },
    {
      name: 'product',
      label: 'Product',
      isMultiple: false,
      type: 'select',
      values: productData?.data,
      isLoading: isProductLoading,
      rules: [{ required: true, message: 'Please select product' }],
      defaultValue: receivingReportStore.movement?.product?.id,
    },
    {
      name: 'productUom',
      label: 'UoM',
      isMultiple: false,
      type: 'select',
      values: uomData?.data,
      isLoading: isUomLoading,
      defaultValue: receivingReportStore.movement?.productUom?.id,
    },
    {
      name: 'description',
      label: 'Description',
      type: 'input',
    },
    {
      name: 'requestQty',
      label: 'Received Quantity',
      type: 'numeric',
      rules: [{ required: true, message: 'Please enter quantity' }],
      defaultValue: receivingReportStore.movement?.requestQty,
    },
  ];

  return (
    <>
      <div className="py-2 px-4 flex justify-between">
        <div className={'text-base font-semibold leading-6'}>Movements</div>
        <Button onClick={handleOpenModal}>Add Movement</Button>
      </div>
      <div className="p-4">
        <Table
          rowKey={(record) => record.uuid}
          dataSource={receivingReportStore.formData.movements}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        >
          <Table.Column title="Description" dataIndex="description" />
          <Table.Column
            title="Code"
            dataIndex="productCode"
            render={(product, record: RRMovement) => {
              return <span>{record.product?.code}</span>;
            }}
          />
          <Table.Column
            title="Product"
            dataIndex="product"
            render={(product, record: RRMovement) => {
              return <span>{record.product?.description}</span>;
            }}
          />
          <Table.Column
            title="Product UoM"
            dataIndex="productUoM"
            render={(product, record: RRMovement) => {
              return <span>{record.productUom?.description}</span>;
            }}
          />
          <Table.Column
            title="Quantity"
            dataIndex="quantity"
            render={(product, record: RRMovement) => {
              return <span>{roundAndFormat(record.quantity)}</span>;
            }}
          />
          <Table.Column
            title="Request UoM"
            dataIndex="requestUoM"
            render={(_text, record: RRMovement) => {
              return <span>{record.requestUom?.description}</span>;
            }}
          />
          <Table.Column
            title="Received Quantity"
            dataIndex="requestQty"
            render={(_text, record: RRMovement) => {
              return <span>{roundAndFormat(record.requestQty)}</span>;
            }}
          />
          <Table.Column
            title="Action"
            key="operation"
            fixed="right"
            render={(record: RRMovement) => {
              return (
                <div className="space-x-1">
                  <Tooltip title="Edit">
                    <Button
                      type="text"
                      icon={<EditOutlined style={{ color: '#4080fd' }} />}
                      size="middle"
                      onClick={() => handleEditButtonClick(record)}
                    />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      size="middle"
                      danger
                      onClick={() => handleDeleteButtonClick(record)}
                    />
                  </Tooltip>
                </div>
              );
            }}
          />
        </Table>
      </div>

      <WarningModal
        message={
          'This action cannot be undone and all associated data will be permanently removed from our system.'
        }
        onClose={handleDeleteModalClose}
        visible={isDeleteModalOpen}
        title={'Remove movement item?'}
        onConfirm={handleDeleteModalConfirm}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={'Movement item successfully removed'}
        onClose={handleInfoModalClose}
      />

      <ModalForm
        visible={modalVisible}
        onClose={handleCloseModal}
        formFields={fields}
        rowCol={2}
        form={form}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        data={receivingReportStore.movement}
        setData={receivingReportStore.setMovement}
      />
    </>
  );
};

export default ReceivingReportMovementUpdate;
